import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PaymentusResponse {
  Token: string;
  Type: string;
  MaskedAccountNumber: string;
  CardHolderName: string;
  ExpiryDate: string;
  ZipCode: string;
  Default: string;
}

export interface SavedPaymentMethod {
  mplPolicyId?: string;
  mplGuid?: string;
  paymentusResponse: PaymentusResponse;
}

@Injectable({
  providedIn: 'root',
})
export class SavedPaymentMethodService {
  private readonly storageKey = 'savedPaymentMethod';
  private readonly savedPaymentMethodSource = new BehaviorSubject<SavedPaymentMethod | null>(
    this.getFromSessionStorage()
  );

  readonly savedPaymentMethod$: Observable<SavedPaymentMethod | null> = this.savedPaymentMethodSource.asObservable();

  setSavedPaymentMethod(savedPaymentMethod: SavedPaymentMethod): void {
    this.savedPaymentMethodSource.next(savedPaymentMethod);
    this.persistToSessionStorage(savedPaymentMethod);
  }

  getSavedPaymentMethod(): SavedPaymentMethod | null {
    return this.savedPaymentMethodSource.getValue();
  }

  clearSavedPaymentMethod(): void {
    this.savedPaymentMethodSource.next(null);
    sessionStorage.removeItem(this.storageKey);
  }

  patchSavedPaymentMethod(partial: Partial<SavedPaymentMethod>): void {
    const current = this.savedPaymentMethodSource.getValue();

    if (!current) {
      return;
    }

    const updated: SavedPaymentMethod = {
      ...current,
      ...partial,
      paymentusResponse: {
        ...current.paymentusResponse,
        ...partial.paymentusResponse,
      },
    };

    this.setSavedPaymentMethod(updated);
  }

  private persistToSessionStorage(savedPaymentMethod: SavedPaymentMethod): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(savedPaymentMethod));
  }

  private getFromSessionStorage(): SavedPaymentMethod | null {
    const storedValue = sessionStorage.getItem(this.storageKey);

    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as SavedPaymentMethod;
    } catch {
      sessionStorage.removeItem(this.storageKey);
      return null;
    }
  }
}
